/**
 * VDOT calculator UI bindings (esbuild entry point).
 *
 * Reads a PB (distance + finish time), computes VDOT + equivalent times +
 * training paces (pure logic in vdot.ts), and renders a percentile gauge
 * against RunRepeat distributions (pure logic in percentiles.ts).
 *
 * No calculation logic lives here — only DOM read/write and Chart.js setup.
 */

import { copyShareableUrl, getQueryParams, updateQueryParams } from '../shared/calculator';
import type {
  AgeGroup,
  RaceDistance,
  RunnerCohort,
  TrainingPaces,
  VdotResult,
} from '../shared/types';
import { COHORT_LABELS, getDistribution } from './percentile-data';
import {
  compareDistances,
  formatPercentile,
  MAJORS_AGE_GROUPS,
  percentileFor,
  percentileForMajors,
} from './percentiles';
import {
  calculateVdotResult,
  RACE_DISTANCE_LABELS,
} from './vdot';
import type { PercentileComparisonRow, PercentileResult, PercentileSource } from '../shared/types';

/* ------------------------------ chart shim ------------------------------ */

interface ChartInstance {
  destroy(): void;
}
interface ChartDataset {
  label: string;
  // Chart.js accepts sparse arrays (null gaps) and {x,y} point objects
  // (numeric x for category-less line charts, string x otherwise).
  data: Array<number | null | { x: number | string; y: number }>;
  [k: string]: unknown;
}
interface ChartConfig {
  type: string;
  data: { labels: string[]; datasets: ChartDataset[] };
  options: Record<string, unknown>;
}
declare const Chart: new (ctx: HTMLCanvasElement, config: ChartConfig) => ChartInstance;

/* ------------------------------ DOM handles ----------------------------- */

const distanceSelect = document.getElementById('distance') as HTMLSelectElement;
const hoursInput = document.getElementById('hours') as HTMLInputElement;
const minutesInput = document.getElementById('minutes') as HTMLInputElement;
const secondsInput = document.getElementById('seconds') as HTMLInputElement;
const cohortSelect = document.getElementById('cohort') as HTMLSelectElement;
const ageGroupSelect = document.getElementById('age-group') as HTMLSelectElement;
const sourceSelect = document.getElementById('source') as HTMLSelectElement;

const errorBox = document.getElementById('error') as HTMLDivElement;
const resultsWrap = document.getElementById('results') as HTMLDivElement;

const vdotValueEl = document.getElementById('vdot-value') as HTMLParagraphElement;
const vdotTierEl = document.getElementById('vdot-tier') as HTMLParagraphElement;

const pacesGrid = document.getElementById('paces') as HTMLDivElement;
const eqTbody = document.querySelector('#equivalents tbody') as HTMLTableSectionElement;

const pctValueEl = document.getElementById('pct-value') as HTMLParagraphElement;
const pctSummaryEl = document.getElementById('pct-summary') as HTMLParagraphElement;
const pctNoteEl = document.getElementById('pct-note') as HTMLParagraphElement;
const pctGaugeFill = document.getElementById('pct-gauge-fill') as HTMLDivElement;
const pctGaugeLabel = document.getElementById('pct-gauge-label') as HTMLSpanElement;

/* ------------------------------- helpers -------------------------------- */

/** All race distances available in the PB dropdown. */
const ALL_DISTANCES: RaceDistance[] = ['1500m', '1mile', '3K', '5K', '10K', 'half', 'marathon'];

/**
 * Logit transform and its inverse (sigmoid), used to spread out the percentile
 * axis tails. Runner distributions are roughly log-normal, so on a linear 0–100
 * axis the fast tail (90–99%) collapses against the right edge. Plotting x in
 * logit space gives both tails symmetric visual room: 95→99 occupies ~16% of
 * the axis instead of ~4%. Tick labels are converted back to percentiles.
 *
 *   logit(p)    = ln(p/(1−p))   for p in (0,1)
 *   sigmoid(x) = 1/(1+e^−x)     = inverse
 */
function logit(p: number): number {
  return Math.log(p / (1 - p));
}
function sigmoid(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

/** Landmark percentile ticks (as %, 0–100) forced onto the logit-scaled axis. */
const AXIS_LANDMARKS = [1, 5, 10, 25, 50, 75, 90, 95, 99, 99.9] as const;
/**
 * Axis bounds in logit space. Must extend PAST the densest landmarks shown
 * (99.9%) and past where a runner marker can land (the percentile engine clamps
 * ranks to [0.1, 99.9]), so neither the curve nor any marker is clipped off-chart.
 * logit(0.0005) … logit(0.9995) gives ~1% headroom either side of P99.9.
 */
const AXIS_MIN_LOGIT = logit(0.0005);
const AXIS_MAX_LOGIT = logit(0.9995);

/** Parse three number inputs (h, m, s) into total seconds. */
function readTimeSeconds(): number {
  const h = Math.max(0, Math.floor(Number(hoursInput.value) || 0));
  const m = Math.max(0, Math.floor(Number(minutesInput.value) || 0));
  const s = Math.max(0, Math.floor(Number(secondsInput.value) || 0));
  return h * 3600 + m * 60 + s;
}

/** Format seconds as a human finish time: 1500 -> "25:00", 10800 -> "3:00:00". */
function formatTime(totalSeconds: number): string {
  const t = Math.round(totalSeconds);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Format a seconds-per-km pace as "M:SS /km" (and a min/mi in parentheses). */
function formatPace(secPerKm: number): string {
  const km = formatTime(secPerKm);
  const perMile = secPerKm * 1.609344;
  return `${km} /km <span class="fn-pace-mi">(${formatTime(perMile)} /mi)</span>`;
}

/** Map a VDOT to a qualitative training tier (Daniels-style bands). */
function vdotTier(vdot: number): { label: string; color: string } {
  if (vdot >= 75) return { label: 'Elite / world class', color: '#9b59b6' };
  if (vdot >= 65) return { label: 'Sub-elite / advanced', color: '#e67e22' };
  if (vdot >= 55) return { label: 'Highly competitive', color: '#e74c3c' };
  if (vdot >= 45) return { label: 'Competitive regional', color: '#2a9d8f' };
  if (vdot >= 37) return { label: 'Recreational / trained', color: '#3498db' };
  return { label: 'Developing fitness', color: '#7f8c8d' };
}

/* ------------------------------ rendering -------------------------------- */

let percentileChart: ChartInstance | null = null;

function showError(msg: string): void {
  errorBox.textContent = msg;
  errorBox.style.display = 'block';
  resultsWrap.style.display = 'none';
}

function clearError(): void {
  errorBox.style.display = 'none';
  errorBox.textContent = '';
}

function renderPaces(p: TrainingPaces): void {
  const cards: Array<{ name: string; desc: string; pace: number }> = [
    { name: 'Easy', desc: 'Recovery & long runs', pace: p.easyFast },
    { name: 'Marathon', desc: 'Goal marathon pace', pace: p.marathon },
    { name: 'Threshold', desc: 'Comfortably hard, ~1 hr effort', pace: p.threshold },
    { name: 'Interval', desc: 'Hard, ~VO₂max', pace: p.interval },
    { name: 'Repetition', desc: 'Very hard, form/speed', pace: p.repetition },
  ];
  pacesGrid.innerHTML = cards
    .map(
      (c) => `
      <div class="fn-pace-card">
        <p class="fn-pace-name">${c.name}</p>
        <p class="fn-pace-val">${formatPace(c.pace)}</p>
        <p class="fn-pace-desc">${c.desc}</p>
      </div>`,
    )
    .join('');
}

function renderEquivalents(result: VdotResult): void {
  if ('error' in result) return;
  const inputDist = result.input.distance;
  eqTbody.innerHTML = result.equivalents
    .map((e) => {
      const isInput = e.distance === inputDist;
      return `
      <tr${isInput ? ' class="fn-eq-input"' : ''}>
        <td>${e.label}${isInput ? ' <span class="fn-tag">your PB</span>' : ''}</td>
        <td>${formatTime(e.timeSeconds)}</td>
      </tr>`;
    })
    .join('');
}

/**
 * Sample a distribution into a smooth curve of {x, time} points, then map each
 * to a LOGIT-scaled x (x = logit(rank/100)) so the tails spread out.
 *
 * `dist` is the published percentile grid (P1 fastest → P90 slowest); we
 * interpolate times across grid percentiles, then rank = 100 − gridPct.
 *
 * Sampling grid: dense in the tails, coarser in the middle. Integer percentiles
 * alone leave gaps in the fast tail (no point at 99.5) so a marker there would
 * float off the line; we therefore sample the landmarks [95, 99, 99.5, 99.9]
 * and their mirror [0.1, 0.5, 1, 5] explicitly, plus a 0.5-step sweep of the
 * 90–100 region, on top of a 5-point base grid.
 *
 * Returns points ordered by x ASCENDING (left = back of pack, right = elite),
 * with duplicates removed.
 */
function sampleCurve(
  dist: Array<{ percentile: number; timeSeconds: number }>,
): Array<{ x: number; y: number }> {
  const pts = [...dist].sort((a, b) => a.timeSeconds - b.timeSeconds); // fastest first

  // Grid percentiles at which to sample the time. Built from three layers:
  //  - base grid: every 5% (well-sampled body of the distribution)
  //  - dense tail: every 0.5% from 90 to 100, and 0 to 10 (where curves bend)
  //  - exact landmarks: 95, 99, 99.5, 99.9 (must align with possible marker ranks)
  const gridPercentiles = new Set<number>();
  for (let p = 0; p <= 100; p += 5) gridPercentiles.add(p);
  for (let p = 90; p <= 100; p += 0.5) gridPercentiles.add(p);
  for (let p = 0; p <= 10; p += 0.5) gridPercentiles.add(p);
  for (const p of [95, 99, 99.5, 99.9, 0.1, 0.5, 1, 5]) gridPercentiles.add(p);

  /** Linear-interpolate the finish time at a given grid percentile p (0–100). */
  const timeAtGrid = (p: number): number => {
    if (p <= pts[0]!.percentile) return pts[0]!.timeSeconds;
    if (p >= pts[pts.length - 1]!.percentile) return pts[pts.length - 1]!.timeSeconds;
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      if (p >= a.percentile && p <= b.percentile) {
        const frac = (p - a.percentile) / (b.percentile - a.percentile);
        return a.timeSeconds + frac * (b.timeSeconds - a.timeSeconds);
      }
    }
    return pts[pts.length - 1]!.timeSeconds;
  };

  const out: Array<{ x: number; y: number }> = [];
  for (const p of gridPercentiles) {
    const rank = 100 - p; // higher rank = faster
    // Clamp just shy of the asymptotes (0,1) so logit stays finite. Headroom
    // well beyond the 99.9 axis max, so elite markers never collapse together.
    const clamped = Math.max(0.0001, Math.min(0.9999, rank / 100));
    out.push({ x: logit(clamped), y: timeAtGrid(p) });
  }
  // Sort ascending by x; Chart.js draws the line through points in array order.
  out.sort((a, b) => a.x - b.x);
  return out;
}

/**
 * Render the hero percentile stat from a precomputed PercentileResult (or null
 * when no distribution is available for the selected source/cohort/distance).
 * Source-agnostic: works for both 'majors' and 'all' results.
 */
function renderPercentile(res: PercentileResult | null): void {
  if (!res) {
    pctValueEl.textContent = '—';
    pctSummaryEl.textContent = 'No population data for this distance/source combination.';
    pctNoteEl.textContent =
      'World Majors age-graded percentiles are marathon-only. Switch the baseline to '
      + '“All runners” for 5K / 10K / half-marathon.';
    pctGaugeFill.style.width = '0%';
    pctGaugeLabel.textContent = '';
    if (percentileChart) {
      percentileChart.destroy();
      percentileChart = null;
    }
    return;
  }

  const pct = res.percentile;
  pctValueEl.textContent = formatPercentile(pct);
  pctSummaryEl.textContent = res.summary;
  pctNoteEl.textContent = '';

  // Animated gauge bar (filled = % of field you beat).
  pctGaugeFill.style.width = `${Math.max(2, Math.min(100, pct))}%`;
  pctGaugeLabel.textContent = `faster than ${formatPercentile(pct)}`;
}

/**
 * Render the multi-distance distribution chart: one finish-time curve per
 * distance (5K / 10K / half / marathon), all on the same axes — x = percentile
 * rank (0 = back of pack on the left, 100 = elite on the right), y = finish
 * time. A red marker is placed on each curve exactly where the runner sits,
 * so you can see your position on every distance's distribution at once.
 *
 * The PB distance uses the runner's actual finish time; the others use the
 * VDOT-projected equivalent time (flagged in the tooltip).
 */
function renderDistributionChart(
  inputDistance: RaceDistance,
  cohort: RunnerCohort,
  comparison: PercentileComparisonRow[],
): void {
  const ctx = document.getElementById('pct-chart') as HTMLCanvasElement;

  // Distances to plot: only those with a distribution AND a known runner time/rank.
  const plottable = comparison.filter(
    (r): r is PercentileComparisonRow & { percentile: number } => r.percentile !== null,
  );

  // One line dataset per distance. Distances are sorted so longer races (with
  // higher finish times) layer cleanly; the input distance is highlighted.
  const distanceOrder: RaceDistance[] = ['5K', '10K', 'half', 'marathon'];
  const palette: Record<RaceDistance, string> = {
    '5K': '#3498db',
    '10K': '#2a9d8f',
    half: '#e67e22',
    marathon: '#9b59b6',
    '1500m': '#888',
    '1mile': '#888',
    '3K': '#888',
  };

  const lineDatasets: ChartDataset[] = [];
  // Each marker: x = logit-space position (for the chart), rank = 0–100
  // percentile (for display in the tooltip). Both are needed because the axis
  // is logit-scaled but labels read in plain percent.
  const markerPoints: Array<{
    x: number;
    y: number;
    rank: number;
    distance: RaceDistance;
    projected: boolean;
  }> = [];

  for (const distance of distanceOrder) {
    const dist = getDistribution(distance, cohort);
    if (!dist) continue;
    const isInput = distance === inputDistance;
    const curve = sampleCurve(dist);
    lineDatasets.push({
      label: `${RACE_DISTANCE_LABELS[distance]}`,
      data: curve,
      borderColor: palette[distance],
      backgroundColor: isInput ? `${palette[distance]}22` : 'transparent',
      fill: isInput,
      tension: 0.35,
      pointRadius: 0,
      pointHoverRadius: 0,
      borderWidth: isInput ? 3 : 1.8,
      borderDash: isInput ? [] : [4, 4],
    });
    // Find the runner's marker on this distance's curve.
    const row = plottable.find((r) => r.distance === distance);
    if (row) {
      const clampedRank = Math.max(0.1, Math.min(99.9, row.percentile));
      markerPoints.push({
        x: logit(clampedRank / 100), // position in logit space
        y: row.timeSeconds,
        rank: row.percentile, // plain 0–100 for the tooltip label
        distance,
        projected: row.projected,
      });
    }
  }

  // One scatter dataset holding all the runner's markers, so a single legend
  // entry ("You") covers them all.
  const markerDataset: ChartDataset = {
    label: 'You',
    data: markerPoints.map((p) => ({ x: p.x, y: p.y })),
    showLine: false,
    pointRadius: 7,
    pointHoverRadius: 9,
    pointBackgroundColor: '#e74c3c',
    pointBorderColor: '#fff',
    pointBorderWidth: 2,
    // Carry the per-point distance/projected info for the tooltip via a side table.
    _markerMeta: markerPoints,
  };

  if (percentileChart) percentileChart.destroy();
  percentileChart = new Chart(ctx, {
    type: 'line',
    data: { labels: [], datasets: [...lineDatasets, markerDataset] },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 500 },
      interaction: { mode: 'nearest', intersect: false, axis: 'x' },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            font: { size: 12 },
            usePointStyle: true,
          },
        },
        tooltip: {
          callbacks: {
            title: (items: Array<{ parsed: { x: number } }>) => {
              const xLogit = items[0]?.parsed.x;
              if (typeof xLogit !== 'number') return '';
              // x is in logit space; convert back to a percentile for the label.
              const rank = sigmoid(xLogit) * 100;
              return `Faster than ${formatPercentile(rank)} of runners`;
            },
            label: (item: { dataset: ChartDataset & { _markerMeta?: typeof markerPoints }; parsed: { x: number; y: number } }) => {
              // Marker dataset -> show this distance's time + projected flag.
              if (item.dataset._markerMeta) {
                // Hit-test in logit space (both parsed.x and meta.x are logit-scaled).
                const hit = item.dataset._markerMeta.find(
                  (m) => Math.abs(m.x - item.parsed.x) < 0.4 && Math.abs(m.y - item.parsed.y) < 1,
                );
                if (hit) {
                  const tag = hit.projected ? ' (VDOT projected)*' : ' (your PB)';
                  return `${RACE_DISTANCE_LABELS[hit.distance]}: ${formatTime(hit.y)}${tag}`;
                }
              }
              return `${item.dataset.label}: ${formatTime(item.parsed.y)}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: AXIS_MIN_LOGIT,
          max: AXIS_MAX_LOGIT,
          title: {
            display: true,
            text: 'Percentile rank — logit-scaled so the top end spreads out  (← back of pack · elite →)',
          },
          // Force ticks at exact landmark percentiles. The data lives in logit
          // space, so each tick value is logit(p/100); the callback converts
          // it back to a percentile string. afterBuildTicks is the only Chart.js
          // mechanism that places ticks at exact data values (callback only
          // relabels auto-ticks).
          afterBuildTicks: (scale: { ticks: Array<{ value: number }> }) => {
            scale.ticks = AXIS_LANDMARKS.map((p) => ({ value: logit(p / 100) }));
          },
          ticks: {
            autoSkip: false,
            callback: (val: unknown) => {
              const p = sigmoid(Number(val)) * 100;
              if (!Number.isFinite(p)) return '';
              return formatPercentile(p);
            },
          },
        },
        y: {
          title: { display: true, text: 'Finish time' },
          ticks: { callback: (v: number | string) => formatTime(Number(v)) },
        },
      },
    },
  });
}

/**
 * Render the World-Majors age-graded chart: a single marathon distribution
 * curve for the selected gender × age-group, with the runner's marker on it.
 * Uses the same logit-scaled axis as the multi-distance chart.
 */
function renderMajorsChart(
  marathonTime: number,
  cohort: RunnerCohort,
  ageGroup: AgeGroup,
  hero: PercentileResult | null,
): void {
  const ctx = document.getElementById('pct-chart') as HTMLCanvasElement;
  if (!hero || !hero.distribution.length) {
    if (percentileChart) {
      percentileChart.destroy();
      percentileChart = null;
    }
    return;
  }
  const curve = sampleCurve(hero.distribution);
  const rank = hero.percentile;
  const sexLabel = cohort === 'men' ? 'male' : 'female';
  const dataset: ChartDataset = {
    label: `Marathon — ${sexLabel}, ${ageGroup} (World Majors)`,
    data: curve,
    borderColor: '#9b59b6',
    backgroundColor: 'rgba(155,89,182,0.15)',
    fill: true,
    tension: 0.35,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderWidth: 3,
  };
  const marker: ChartDataset = {
    label: 'You',
    data: [{ x: logit(Math.max(0.0001, Math.min(0.9999, rank / 100))), y: marathonTime }],
    showLine: false,
    pointRadius: 8,
    pointHoverRadius: 10,
    pointBackgroundColor: '#e74c3c',
    pointBorderColor: '#fff',
    pointBorderWidth: 2,
  };
  if (percentileChart) percentileChart.destroy();
  percentileChart = new Chart(ctx, {
    type: 'line',
    data: { labels: [], datasets: [dataset, marker] },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 500 },
      interaction: { mode: 'nearest', intersect: false, axis: 'x' },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { boxWidth: 12, font: { size: 12 }, usePointStyle: true },
        },
        tooltip: {
          callbacks: {
            title: (items: Array<{ parsed: { x: number } }>) => {
              const xLogit = items[0]?.parsed.x;
              if (typeof xLogit !== 'number') return '';
              return `Faster than ${formatPercentile(sigmoid(xLogit) * 100)} of runners`;
            },
            label: (item: { dataset: ChartDataset; parsed: { y: number } }) =>
              `${item.dataset.label}: ${formatTime(item.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: AXIS_MIN_LOGIT,
          max: AXIS_MAX_LOGIT,
          title: {
            display: true,
            text: 'Percentile rank — logit-scaled  (← back of pack · elite →)',
          },
          afterBuildTicks: (scale: { ticks: Array<{ value: number }> }) => {
            scale.ticks = AXIS_LANDMARKS.map((p) => ({ value: logit(p / 100) }));
          },
          ticks: {
            autoSkip: false,
            callback: (val: unknown) => {
              const p = sigmoid(Number(val)) * 100;
              if (!Number.isFinite(p)) return '';
              return formatPercentile(p);
            },
          },
        },
        y: {
          title: { display: true, text: 'Marathon finish time' },
          ticks: { callback: (v: number | string) => formatTime(Number(v)) },
        },
      },
    },
  });
}

function render(): void {
  clearError();
  const timeSeconds = readTimeSeconds();
  const distance = distanceSelect.value as RaceDistance;
  const cohort = cohortSelect.value as RunnerCohort;

  if (timeSeconds <= 0) {
    showError('Enter a finish time greater than zero.');
    return;
  }

  const result = calculateVdotResult({ distance, timeSeconds });
  if ('error' in result) {
    showError(result.error);
    return;
  }

  resultsWrap.style.display = 'block';

  // VDOT headline + tier.
  vdotValueEl.textContent = result.vdot.toFixed(1);
  const tier = vdotTier(result.vdot);
  vdotTierEl.textContent = tier.label;
  vdotTierEl.style.color = tier.color;

  renderPaces(result.paces);
  renderEquivalents(result);

  updateQueryParams({
    distance,
    h: hoursInput.value,
    m: minutesInput.value,
    s: secondsInput.value,
    cohort,
  });

  const source = sourceSelect.value as PercentileSource;
  const ageGroup = ageGroupSelect.value as AgeGroup;
  const cohortForPct: RunnerCohort = source === 'majors' && cohort === 'overall' ? 'men' : cohort;

  updateQueryParams({
    distance,
    h: hoursInput.value,
    m: minutesInput.value,
    s: secondsInput.value,
    cohort,
    age: ageGroup,
    src: source,
  });

  // Hero percentile + distribution chart. The two sources have different
  // shapes: Majors is marathon-only age-graded; 'all' is multi-distance.
  if (source === 'majors') {
    // Majors is marathon-only. Use the actual PB if it's a marathon, else the
    // VDOT-projected marathon equivalent.
    const isMarathonPB = distance === 'marathon';
    const marathonTime = isMarathonPB
      ? timeSeconds
      : result.equivalents.find((e) => e.distance === 'marathon')?.timeSeconds ?? timeSeconds;
    const hero = percentileForMajors(marathonTime, cohortForPct, ageGroup);
    if (!isMarathonPB && hero) {
      // Flag that the marathon time is projected, not an actual PB.
      hero.summary += ' (marathon time projected from your VDOT)*';
    }
    renderPercentile(hero);
    renderMajorsChart(marathonTime, cohortForPct, ageGroup, hero);
  } else {
    // 'all' source: hero on the actual PB distance, multi-distance chart.
    const hero = percentileFor(timeSeconds, distance, cohort);
    renderPercentile(hero);
    const comparison = compareDistances(distance, timeSeconds, result.equivalents, cohort);
    renderDistributionChart(distance, cohort, comparison);
  }
}

/* ----------------------------- initialization --------------------------- */

// Restore from query params.
const params = getQueryParams({
  distance: '5K',
  h: '0',
  m: '25',
  s: '0',
  cohort: 'men',
  age: '30-34',
  src: 'majors',
});
distanceSelect.value = ALL_DISTANCES.includes(params.distance as RaceDistance)
  ? params.distance
  : '5K';
hoursInput.value = params.h;
minutesInput.value = params.m;
secondsInput.value = params.s;
cohortSelect.value = params.cohort;
ageGroupSelect.value = (MAJORS_AGE_GROUPS as readonly string[]).includes(params.age)
  ? params.age
  : '30-34';
sourceSelect.value = params.src === 'all' ? 'all' : 'majors';

// Wire events.
[
  distanceSelect,
  hoursInput,
  minutesInput,
  secondsInput,
  cohortSelect,
  ageGroupSelect,
  sourceSelect,
].forEach((el) => {
  el.addEventListener('input', render);
  el.addEventListener('change', render);
});

document.getElementById('share-btn')?.addEventListener('click', () => {
  copyShareableUrl({
    distance: distanceSelect.value,
    h: hoursInput.value,
    m: minutesInput.value,
    s: secondsInput.value,
    cohort: cohortSelect.value,
    age: ageGroupSelect.value,
    src: sourceSelect.value,
  }).then(() => {
    const btn = document.getElementById('share-btn') as HTMLButtonElement;
    btn.textContent = '✅ Copied!';
    setTimeout(() => {
      btn.textContent = '📋 Copy shareable link';
    }, 2000);
  });
});

render();
